import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PauseIcon from "@mui/icons-material/Pause";
import SettingsIcon from "@mui/icons-material/Settings";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Confetti from "react-confetti";
import SlidingPlayMain from "../../admin/story/SlidingPlayMain";
import slidingPuzzleQuestIcon from "../../assets/app-icons/sliding-puzzle-quest-app-icon.png";
import { SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL } from "../../config/nativeAppsVisibility";
import { usePagesTranslation } from "../locales";
import LandingPhoneStoreCta from "./LandingPhoneStoreCta";

const TEXT = "#F4F7FB";
const TEXT_MUTED = "rgba(214, 226, 240, 0.68)";
const CHIP_BG = "rgba(46, 196, 182, 0.16)";
const BAR_BG = "rgba(255, 255, 255, 0.08)";
const CONFETTI_MS = 7200;
const COMPLETE_HOLD_MS = 2200;
const SLIDE_MS = 420;
const GAP_MS = 160;
const INITIAL_DELAY_MS = 700;
const SOLVE_BEAT_MS = 700;

const actionBtnSx = {
    minWidth: 32,
    minHeight: 32,
    width: 32,
    height: 32,
    p: 0,
    borderRadius: "10px",
    color: TEXT,
    bgcolor: CHIP_BG,
    "& .MuiSvgIcon-root": { fontSize: "1.1rem" },
};

function formatPlayClock(totalSeconds) {
    const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
}

function seededRng(seed) {
    let state = 0;
    const str = String(seed || "sliding-hero");
    for (let i = 0; i < str.length; i += 1) {
        state = (Math.imul(31, state) + str.charCodeAt(i)) | 0;
    }
    if (state === 0) state = 1;
    return () => {
        state = Math.imul(state ^ (state >>> 16), 2246822507);
        state = Math.imul(state ^ (state >>> 13), 3266489909);
        state ^= state >>> 16;
        return (state >>> 0) / 4294967296;
    };
}

function neighborSlots(emptyPos, gs) {
    const row = Math.floor(emptyPos / gs);
    const col = emptyPos % gs;
    const next = [];
    if (row > 0) next.push(emptyPos - gs);
    if (row < gs - 1) next.push(emptyPos + gs);
    if (col > 0) next.push(emptyPos - 1);
    if (col < gs - 1) next.push(emptyPos + 1);
    return next;
}

function swapSlots(layout, a, b) {
    const next = layout.slice();
    const tmp = next[a];
    next[a] = next[b];
    next[b] = tmp;
    return next;
}

/** Scramble from solved with a short random walk so the reverse path is a legal 1-by-1 solve. */
function buildSolveDemo(gs, seed) {
    const n = gs * gs;
    const identity = Array.from({ length: n }, (_, i) => i);
    if (gs < 2) {
        return { start: identity, steps: [] };
    }
    const rng = seededRng(seed);
    const walk = gs <= 3 ? 10 : 16;
    let layout = identity.slice();
    let emptyPos = n - 1;
    let prevEmpty = -1;
    const frames = [];
    for (let i = 0; i < walk; i += 1) {
        const options = neighborSlots(emptyPos, gs).filter((pos) => pos !== prevEmpty);
        const pool = options.length > 0 ? options : neighborSlots(emptyPos, gs);
        const tilePos = pool[Math.floor(rng() * pool.length) % pool.length];
        layout = swapSlots(layout, emptyPos, tilePos);
        frames.push(layout);
        prevEmpty = emptyPos;
        emptyPos = tilePos;
    }
    const steps = [];
    for (let i = frames.length - 2; i >= 0; i -= 1) {
        steps.push(frames[i]);
    }
    steps.push(identity);
    return { start: frames[frames.length - 1] || identity, steps };
}

function Stat({ label, value }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
            <Typography
                component="span"
                sx={{
                    fontSize: "0.55rem",
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: TEXT_MUTED,
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Typography>
            <Typography
                component="span"
                sx={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    color: TEXT,
                    lineHeight: 1.15,
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

/** Android Sliding Puzzle Quest play chrome: title, timer/moves toolbar, board. */
function LandingSlidingPlayChrome({ title, clock, moves }) {
    return (
        <>
            <Box
                sx={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                    px: 0.5,
                    minHeight: 36,
                }}
            >
                <IconButton size="small" tabIndex={-1} aria-hidden sx={{ ...actionBtnSx, bgcolor: "transparent" }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography
                    noWrap
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        color: TEXT,
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Box
                sx={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.75,
                    mx: 0.75,
                    mb: 0.75,
                    px: 1,
                    py: 0.4,
                    minHeight: 44,
                    borderRadius: "12px",
                    bgcolor: BAR_BG,
                    border: "1px solid rgba(255,255,255,0.12)",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Stat label="Time" value={clock} />
                    <IconButton size="small" tabIndex={-1} aria-hidden sx={actionBtnSx}>
                        <PauseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Stat label="Moves" value={String(moves)} />
                    <IconButton size="small" tabIndex={-1} aria-hidden sx={actionBtnSx}>
                        <LightbulbOutlinedIcon />
                    </IconButton>
                    <IconButton size="small" tabIndex={-1} aria-hidden sx={actionBtnSx}>
                        <SettingsIcon />
                    </IconButton>
                </Box>
            </Box>
        </>
    );
}

/** Sliding Puzzle Quest play: scrambled start, then tiles slide home one by one. */
export default function LandingSlidingPlayPreview({
    imageUrl,
    gridSize = 4,
    title = "Sliding Puzzle Quest",
    active = true,
    onBusyChange,
    onPlayComplete,
}) {
    const { t } = usePagesTranslation();
    const gs = Number(gridSize) > 0 ? Number(gridSize) : 4;
    const demo = useMemo(
        () => buildSolveDemo(gs, `${imageUrl || "sliding"}-${gs}`),
        [gs, imageUrl],
    );

    const [layout, setLayout] = useState(demo.start);
    const [moves, setMoves] = useState(0);
    const [elapsedSec, setElapsedSec] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [confettiActive, setConfettiActive] = useState(false);
    const [frameSize, setFrameSize] = useState({ w: 320, h: 480 });
    const [ready, setReady] = useState(false);

    const rootRef = useRef(null);
    const onBusyChangeRef = useRef(onBusyChange);
    onBusyChangeRef.current = onBusyChange;
    const onPlayCompleteRef = useRef(onPlayComplete);
    onPlayCompleteRef.current = onPlayComplete;

    useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el) return undefined;
        const apply = () => {
            setFrameSize({
                w: Math.max(1, el.clientWidth),
                h: Math.max(1, el.clientHeight),
            });
        };
        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!active || !imageUrl) {
            setLayout(demo.start);
            setMoves(0);
            setElapsedSec(0);
            setCompleted(false);
            setConfettiActive(false);
            setReady(false);
            return undefined;
        }
        setLayout(demo.start);
        setMoves(0);
        setElapsedSec(0);
        setCompleted(false);
        setConfettiActive(false);
        setReady(false);
        const timer = window.setTimeout(() => setReady(true), 120);
        return () => window.clearTimeout(timer);
    }, [active, imageUrl, demo]);

    useEffect(() => {
        if (!active) return undefined;
        onBusyChangeRef.current?.(true);
        return () => onBusyChangeRef.current?.(false);
    }, [active]);

    useEffect(() => {
        if (!active || !imageUrl || !ready || completed) return undefined;
        const queue = [...demo.steps];
        let gapTimer = 0;
        const playNext = () => {
            const next = queue.shift();
            if (!next) {
                gapTimer = window.setTimeout(() => setCompleted(true), SOLVE_BEAT_MS);
                return;
            }
            setLayout(next);
            setMoves((count) => count + 1);
            gapTimer = window.setTimeout(playNext, SLIDE_MS + GAP_MS);
        };
        const startTimer = window.setTimeout(playNext, INITIAL_DELAY_MS);
        return () => {
            window.clearTimeout(startTimer);
            window.clearTimeout(gapTimer);
        };
    }, [active, imageUrl, ready, completed, demo]);

    useEffect(() => {
        if (!active || !imageUrl || !ready || completed) return undefined;
        const startedAt = Date.now();
        setElapsedSec(0);
        const tick = window.setInterval(() => {
            setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
        }, 250);
        return () => window.clearInterval(tick);
    }, [active, imageUrl, ready, completed]);

    useEffect(() => {
        if (!active || !completed) return undefined;
        setConfettiActive(true);
        const confettiTimer = window.setTimeout(() => setConfettiActive(false), CONFETTI_MS);
        const busyTimer = window.setTimeout(
            () => {
                onBusyChangeRef.current?.(false);
                onPlayCompleteRef.current?.();
            },
            CONFETTI_MS + COMPLETE_HOLD_MS,
        );
        return () => {
            window.clearTimeout(confettiTimer);
            window.clearTimeout(busyTimer);
        };
    }, [active, completed]);

    if (!imageUrl) return null;

    return (
        <Box
            ref={rootRef}
            sx={{
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background: "linear-gradient(180deg, #12203A 0%, #0B1424 48%, #070E1A 100%)",
                pointerEvents: "none",
                overflow: "hidden",
                pt: "26px",
                px: 0.5,
                pb: 0.75,
                boxSizing: "border-box",
                color: TEXT,
            }}
        >
            <LandingSlidingPlayChrome
                title={title}
                clock={formatPlayClock(elapsedSec)}
                moves={moves}
            />
            <Box
                sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.75,
                }}
            >
                <Box sx={{ width: "100%", maxHeight: "100%", aspectRatio: "1 / 1" }}>
                    <SlidingPlayMain
                        imageUrl={imageUrl}
                        gridSize={gs}
                        startingLayout={layout}
                        fillParent
                        readOnly
                        playMoveSound={false}
                        hideCompletedCaption
                        showMatchCelebrations={false}
                        slideTransitionMs={SLIDE_MS}
                        solidEmptySlot
                        gridGutterColor="rgba(0, 0, 0, 0.52)"
                        gridBorderColor="rgba(46, 196, 182, 0.35)"
                    />
                </Box>
            </Box>
            {completed ? (
                <Box
                    sx={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 0.75,
                        px: 1,
                    }}
                >
                    <LandingPhoneStoreCta
                        iconUrl={slidingPuzzleQuestIcon}
                        appName="Sliding Puzzle Quest"
                        playUrl={SLIDING_PUZZLE_QUEST_GOOGLE_PLAY_URL}
                        ariaLabel={t(
                            "landing.sliding-google-play-badge-aria",
                            "Get Sliding Puzzle Quest on Google Play (opens in a new tab)",
                        )}
                        tone="dark"
                    />
                </Box>
            ) : null}
            {confettiActive ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 12,
                        pointerEvents: "none",
                        overflow: "hidden",
                    }}
                >
                    <Confetti
                        width={frameSize.w}
                        height={frameSize.h}
                        recycle={false}
                        numberOfPieces={320}
                        gravity={0.14}
                        tweenDuration={6400}
                        confettiSource={{ x: 0, y: 0, w: frameSize.w, h: 18 }}
                        style={{ pointerEvents: "none" }}
                    />
                </Box>
            ) : null}
        </Box>
    );
}
